import { useEffect, useMemo, useState } from "react";
import { useLingui } from "@lingui/react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import { ActivityType, getActivityTypeLabels } from "@/types/activities";
import { cn } from "@/lib/utils";

interface ActivityTypeSelectorProps {
  value?: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

// All activity types in a flat list (no categories)
const ALL_ACTIVITY_TYPES = [
  ActivityType.ActivityTypeCheckIn,
  ActivityType.ActivityTypeCheckOut,
  ActivityType.ActivityTypeToiletPee,
  ActivityType.ActivityTypeToiletPoo,
  ActivityType.ActivityTypeToiletNothing,
  ActivityType.ActivityTypeFeedingMilk,
  ActivityType.ActivityTypeFeedingPorridge,
  ActivityType.ActivityTypeFeedingSolid,
  ActivityType.ActivityTypeFeedingSnack,
  ActivityType.ActivityTypeFeedingBreastmilk,
  ActivityType.ActivityTypeFeedingRefused,
  ActivityType.ActivityTypeNapStart,
  ActivityType.ActivityTypeNapEnd,
  ActivityType.ActivityTypeHealthAccident,
  ActivityType.ActivityTypeHealthIllness,
  ActivityType.ActivityTypeHealthMedication,
  ActivityType.ActivityTypePicture,
  ActivityType.ActivityTypeNote,
  ActivityType.ActivityTypeMoodCheck,
];

/**
 * Activity Type Selector Component
 *
 * Searchable dropdown for selecting activity types.
 * Uses Command component for searchable, accessible dropdown.
 * No category headers - flat list for easier search.
 */
export function ActivityTypeSelector({
  value,
  onValueChange,
  disabled = false,
  placeholder,
}: ActivityTypeSelectorProps) {
  const { i18n } = useLingui();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const defaultPlaceholder = i18n._({
    id: "activities.form.selectActivityType",
    message: "Select activity type",
  });

  // Get localized activity type labels
  const activityTypeLabels = getActivityTypeLabels(i18n);

  // Debounce search query with 300ms delay
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(id);
  }, [searchQuery]);

  // Reset search query when popover closes
  useEffect(() => {
    if (!open) setSearchQuery("");
  }, [open]);

  // Filter activity types based on search query
  const filteredTypes = useMemo(() => {
    if (!debouncedQuery) return ALL_ACTIVITY_TYPES;

    const query = debouncedQuery.toLowerCase();
    return ALL_ACTIVITY_TYPES.filter((type) => {
      const label = activityTypeLabels[type] || "";
      return label.toLowerCase().includes(query);
    });
  }, [debouncedQuery, activityTypeLabels]);

  // Get the label for the selected value
  const selectedLabel = value ? activityTypeLabels[value] : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          {selectedLabel ? (
            <span className="truncate">{selectedLabel}</span>
          ) : (
            <span className="text-muted-foreground">
              {placeholder || defaultPlaceholder}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={i18n._({
              id: "activities.form.searchActivityType",
              message: "Search activity types...",
            })}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>
              {i18n._({
                id: "activities.form.noActivityTypeFound",
                message: "No activity type found",
              })}
            </CommandEmpty>
            <CommandGroup>
              {filteredTypes.map((type) => (
                <CommandItem
                  key={type}
                  value={type}
                  onSelect={() => {
                    onValueChange(type === value ? "" : type);
                    setOpen(false);
                  }}
                  className="flex items-center justify-between"
                >
                  <span>{activityTypeLabels[type]}</span>
                  {value === type && (
                    <Check className={cn("h-4 w-4 text-primary")} />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
